import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { CheckboxInput } from '@astryxdesign/core/CheckboxInput';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Icon } from '@astryxdesign/core/Icon';
import { MultiSelector } from '@astryxdesign/core/MultiSelector';
import { NumberInput } from '@astryxdesign/core/NumberInput';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { Plus } from 'lucide-react';
import type { AlertTemplate } from '@qronstat/shared';
import { useApp } from '../../app/context';
import type { TeamRuleSample } from '../../api/telegram';
import { formatValue } from '../../model/format';
import { text } from '../../model/meta';
import { RuleEditor } from '../alerts/RuleEditor';
import type { RuleDraft } from '../alerts/RuleEditor';
import { describeRule } from '../alerts/describe';
import { useDimensionValues } from '../filters/useDimensionValues';
import { useSelectedText } from '../useSelectedText';
import { regionApplies, regionMember } from './regions';

export interface EventChoice {
  key: string;
  group: 'template' | 'team' | 'custom';
  hint: string | null;
  draft: RuleDraft;
  selected: boolean;
}

export function templateChoices(
  templates: AlertTemplate[],
  team: TeamRuleSample[],
  intl: IntlShape,
): EventChoice[] {
  return [
    ...templates.map((t) => ({
      key: `template:${t.code}`,
      group: 'template' as const,
      hint: intl.formatMessage({ id: `notifications.hint.${t.code}` }),
      selected: false,
      draft: {
        name: intl.formatMessage({ id: `alerts.template.${t.code}` }),
        template: t.code,
        definition: { filters: [], realOnly: true, respectSchedule: false, ...t.definition },
        enabled: true,
        cooldownMinutes: t.cooldownMinutes,
        notifyRecovery: true,
      },
    })),
    ...team.map((r) => ({
      key: `team:${r.id}`,
      group: 'team' as const,
      hint: null,
      selected: false,
      draft: {
        name: r.name,
        template: r.template,
        definition: r.definition,
        enabled: true,
        cooldownMinutes: r.cooldownMinutes,
        notifyRecovery: r.notifyRecovery,
      },
    })),
  ];
}

export const blankDraft: RuleDraft = {
  name: '',
  template: null,
  definition: {
    metric: 'rents.count',
    filters: [],
    realOnly: true,
    window: { kind: 'today' },
    condition: { kind: 'deviation', direction: 'below', percent: 30, weeks: 4 },
    respectSchedule: false,
  },
  enabled: true,
  cooldownMinutes: 60,
  notifyRecovery: true,
};

function PrimaryParam({ draft, onChange }: { draft: RuleDraft; onChange: (draft: RuleDraft) => void }) {
  const intl = useIntl();
  const { meta, locale } = useApp();
  const metric = meta?.metric(draft.definition.metric);
  const condition = draft.definition.condition;
  const setCondition = (next: typeof condition) =>
    onChange({ ...draft, definition: { ...draft.definition, condition: next } });
  if (condition.kind === 'threshold') {
    const percent = metric?.format === 'percent';
    const shown = percent ? Math.round(condition.value * 10_000) / 100 : condition.value;
    return (
      <NumberInput
        label={intl.formatMessage(
          { id: 'notifications.thresholdLabel' },
          {
            op: intl.formatMessage({
              id: `filters.op.${condition.operator === 'eq' ? 'equals' : condition.operator}`,
            }),
          },
        )}
        description={formatValue(condition.value, metric?.format ?? 'number', locale)}
        size="sm"
        width={180}
        value={shown}
        onChange={(v: number) => setCondition({ ...condition, value: percent ? v / 100 : v })}
      />
    );
  }
  if (condition.kind === 'deviation')
    return (
      <NumberInput
        label={intl.formatMessage({ id: `notifications.deviationLabel.${condition.direction}` })}
        size="sm"
        width={180}
        min={1}
        max={1000}
        value={condition.percent}
        onChange={(v: number) => setCondition({ ...condition, percent: Math.max(1, Math.min(1000, v)) })}
      />
    );
  return null;
}

export function EventPicker({
  choices,
  onChange,
  regions,
  onRegionsChange,
}: {
  choices: EventChoice[];
  onChange: (choices: EventChoice[]) => void;
  regions: string[];
  onRegionsChange: (regions: string[]) => void;
}) {
  const intl = useIntl();
  const selectedText = useSelectedText();
  const { meta, locale } = useApp();
  const [editing, setEditing] = useState<{ key: string | null; draft: RuleDraft } | null>(null);
  const regionDim = meta?.dimension(regionMember);
  const regionValues = useDimensionValues(regionDim, Boolean(regionDim));
  const groups = useMemo(
    () =>
      (['template', 'team', 'custom'] as const)
        .map((group) => ({ group, items: choices.filter((c) => c.group === group) }))
        .filter((g) => g.items.length || g.group === 'custom'),
    [choices],
  );
  const update = (key: string, patch: Partial<EventChoice>) =>
    onChange(choices.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  return (
    <VStack gap={4}>
      {regionDim ? (
        <MultiSelector
          formatValue={selectedText}
          label={intl.formatMessage({ id: 'notifications.regions' })}
          description={intl.formatMessage({ id: 'notifications.regionsHint' })}
          placeholder={intl.formatMessage({ id: 'notifications.allRegions' })}
          hasSearch
          triggerDisplay="labels"
          value={regions}
          onChange={(v: string[]) => onRegionsChange(v)}
          options={regionValues.options}
        />
      ) : null}
      {groups.map(({ group, items }) => (
        <div key={group} data-qs-event-group={group}>
          <VStack gap={2}>
            <Heading level={4}>{intl.formatMessage({ id: `notifications.group.${group}` })}</Heading>
            {items.map((choice) => {
              const metric = meta?.metric(choice.draft.definition.metric);
              const noRegion = regions.length > 0 && !regionApplies(meta, choice.draft.definition.metric);
              return (
                <div key={choice.key} data-qs-event={choice.key}>
                  <Card padding={3}>
                    <VStack gap={2}>
                      <CheckboxInput
                        label={choice.draft.name}
                        value={choice.selected}
                        onChange={(v: boolean) => update(choice.key, { selected: v })}
                      />
                      <Text size="sm" color="secondary">
                        {choice.hint ??
                          describeRule(
                            choice.draft,
                            intl,
                            text(metric?.title, locale),
                            metric?.format ?? 'number',
                            locale,
                          )}
                      </Text>
                      {choice.selected ? (
                        <HStack gap={3} wrap="wrap" vAlign="end">
                          <PrimaryParam
                            draft={choice.draft}
                            onChange={(draft) => update(choice.key, { draft })}
                          />
                          <Button
                            label={intl.formatMessage({ id: 'notifications.moreConditions' })}
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditing({ key: choice.key, draft: choice.draft })}
                          />
                        </HStack>
                      ) : null}
                      {choice.selected && noRegion ? (
                        <Text size="sm" color="secondary">
                          {intl.formatMessage({ id: 'notifications.noRegion' })}
                        </Text>
                      ) : null}
                    </VStack>
                  </Card>
                </div>
              );
            })}
            {group === 'custom' ? (
              <HStack>
                <Button
                  label={intl.formatMessage({ id: 'notifications.addCustom' })}
                  size="sm"
                  icon={<Icon icon={Plus} />}
                  onClick={() => setEditing({ key: null, draft: blankDraft })}
                />
              </HStack>
            ) : null}
          </VStack>
        </div>
      ))}
      {editing ? (
        <RuleEditor
          title={intl.formatMessage({
            id: editing.key ? 'notifications.editEvent' : 'notifications.addCustom',
          })}
          initial={editing.draft}
          mode="personal"
          onClose={() => setEditing(null)}
          onSubmit={async (draft) => {
            if (editing.key) update(editing.key, { draft, selected: true });
            else
              onChange([
                ...choices,
                { key: `custom:${Date.now()}`, group: 'custom', hint: null, draft, selected: true },
              ]);
          }}
        />
      ) : null}
    </VStack>
  );
}
